// webapp/src/lib/checkoutError.ts
//
// FE pure helper — error code → catalogue KEY, for the checkout Edge Function
// response shape (create-booking-checkout + create-checkout-session).
//
// Phase 97 (97-08) : this map used to hold French copy. A pure helper that
// returns copy is a leak that no check can see — the Phase 96 file scan reads
// the page, not `lib/`, and the D-04 harness only mounts the default state,
// where no error is displayed. Same defect class as `lib/phone.ts` (97-07).
// The map now returns `MsgKey`, so the copy lives in the catalogue in both
// languages and a missing key breaks `make typecheck-webapp` before any render.
//
// Covers the 14 codes of create-booking-checkout + the 5 codes that only the
// (now deleted) server-`message` override branch used to cover.
// Refs: phase 97.
import type { MsgKey } from '../i18n/types';

/**
 * Code → catalogue key. CLOSED table, values written by hand.
 *
 * T-97-20 — never compose `` `checkoutError.${code}` ``: `code` comes from the
 * server, and composing would let it choose which catalogue entry is rendered.
 * An unknown code falls back, it does not index.
 */
export const CHECKOUT_ERROR_COPY: Record<string, MsgKey> = {
  'promo_invalid_for_session': 'checkoutError.promo_invalid_for_session',
  'promo_invalid_for_premiere': 'checkoutError.promo_invalid_for_premiere',
  'promo_invalid_for_coaching': 'checkoutError.promo_invalid_for_coaching',
  'promo_invalid_for_pack': 'checkoutError.promo_invalid_for_pack',
  'promo_new_customer_only_violated': 'checkoutError.promo_new_customer_only_violated',
  'booking_not_found': 'checkoutError.booking_not_found',
  'booking_not_held': 'checkoutError.booking_not_held',
  'hold_expired': 'checkoutError.hold_expired',
  'booking_not_owned': 'checkoutError.booking_not_owned',
  'invalid_origin': 'checkoutError.invalid_origin',
  // rate_limited : anon DOS guard (Phase 31.9 SEC-31-03) — create-booking-checkout
  // returns this via the `efError` envelope ({error_code, message}) on HTTP 429.
  // The guard itself is untouched; only the label's source changed.
  'rate_limited': 'checkoutError.rate_limited',
  'stripe_error': 'checkoutError.stripe_error',
  'stripe_no_url': 'checkoutError.stripe_no_url',
  'db_error': 'checkoutError.db_error',
  // --- Les 5 codes que seule la recopie du `message` serveur couvrait (97-08).
  //     Sans eux, sa suppression ferait tomber /packs/checkout/:size sur le repli générique,
  //     y compris pour son erreur de code promo (`conflict`).
  'internal': 'checkoutError.internal', // create-booking-checkout:543, create-checkout-session:425
  'unauthorized': 'checkoutError.unauthorized', // create-checkout-session:95, :99
  'bad_input': 'checkoutError.bad_input', // create-checkout-session:107, :121
  'conflict': 'checkoutError.conflict', // create-checkout-session:212, :225 — deux situations, une clé
  'upstream_error': 'checkoutError.upstream_error', // create-checkout-session:158, :325, :386
};

const FALLBACK_KEY: MsgKey = 'checkoutError.fallback';

export function mapCheckoutError(code: string | undefined | null): MsgKey {
  if (!code) return FALLBACK_KEY;
  return CHECKOUT_ERROR_COPY[code] ?? FALLBACK_KEY;
}

/**
 * Promo-specific error codes routed to the inline promo field (setPromoError)
 * rather than the top-level checkout error banner (setError).
 */
const PROMO_ERROR_CODES = new Set<string>([
  'promo_invalid_for_session',
  'promo_invalid_for_premiere',
  'promo_invalid_for_coaching',
  'promo_invalid_for_pack',
  'promo_new_customer_only_violated',
]);

export interface ExtractedCheckoutError {
  /** Machine-readable code (English) — used for routing + map lookup. */
  code: string;
  /** Catalogue key of the copy to display. The caller resolves it with `t`. */
  messageKey: MsgKey;
  /** True when the error belongs to the inline promo field. */
  isPromo: boolean;
}

/**
 * Normalize a `supabase.functions.invoke` failure into a displayable error.
 *
 * Why this exists (Phase 36/37 triage, 2026-05-22) : supabase-js v2 returns
 * `{ data: null, error: FunctionsHttpError }` on ANY HTTP 4xx/5xx — the response
 * body is NOT in `data`, it lives behind `error.context.json()`. The previous
 * SlotPicker code read `(data ?? {}).error`, which is always `undefined` on an
 * HTTP error, so EVERY checkout error (promo invalid, hold expired, rate-limit,
 * Stripe failure…) silently collapsed to the generic "Impossible d'initier le
 * paiement" copy. This mirrors the correct pattern already used in
 * AdminPage.tsx / AdminCodesPromo.tsx (duck-typed `error.context.json`).
 *
 * Handles BOTH error envelopes create-booking-checkout currently emits:
 *   - legacy `{ error: 'promo_invalid_for_session' }` (most booking/promo paths)
 *   - canonical `efError` `{ error_code: 'rate_limited', message: '…' }` (rate-limit)
 * EF-side envelope normalization is deliberately out of scope here (FE-only fix).
 *
 * Security (SEC-31-10, information disclosure) — Phase 97 (97-08) : the EF
 * `message` used to be displayed when an `error_code` accompanied it. That
 * branch is GONE. Only the `code` is read, and it can only select a key from a
 * closed table, so the disclosure surface is now ZERO strings instead of "one
 * server string, gated". The `message` field stays in the HTTP envelope for
 * debugging — it is simply never read here.
 *
 * MEASURED before deleting (97-08 §interfaces) : none of the 12 `efError` calls
 * of the two reachable Edge Functions interpolates anything (`grep '\${'` →
 * nothing). No delay, no counter, no amount. The client can therefore rebuild
 * 100 % of the text from its catalogue, and nothing is lost.
 *
 * ⚠ Letting the server `message` override the local copy again would
 * re-introduce BOTH the disclosure
 * surface and an untranslatable string. If a future error genuinely needs a
 * dynamic value, add an interpolated catalogue key and pass the value — never
 * the server sentence.
 */
export async function extractCheckoutError(
  invokeError: unknown,
  data: unknown,
): Promise<ExtractedCheckoutError> {
  let code = 'unknown';

  const ctx = (invokeError as { context?: { json?: () => Promise<unknown> } } | null | undefined)?.context;
  if (invokeError && typeof ctx?.json === 'function') {
    // HTTP 4xx/5xx (FunctionsHttpError) — body readable behind error.context.
    try {
      const parsed = (await ctx.json()) as
        | { error_code?: string; error?: string }
        | null;
      code = parsed?.error_code ?? parsed?.error ?? 'unknown';
    } catch {
      code = 'parse_error'; // non-JSON body (raw 500 / 504 / WAF block) → generic copy
    }
  } else if (invokeError) {
    code = 'network_error'; // FunctionsFetchError / relay — no readable .context body
  } else {
    // No invokeError but no data.url — a 2xx body carrying a legacy {error} field,
    // or an empty/malformed success payload.
    const legacy = data as { error?: string; error_code?: string } | null;
    code = legacy?.error_code ?? legacy?.error ?? 'unknown';
  }

  return {
    code,
    messageKey: mapCheckoutError(code),
    isPromo: PROMO_ERROR_CODES.has(code),
  };
}
